import { useState } from "react";

import { JSONTree as JSONTreeOriginal } from "react-json-tree";
import fileDownload from "js-file-download";

import { Tag, Drawer, Button, Table, Spin, Space, Tooltip } from "antd";
import { ZoomInOutlined } from "@ant-design/icons";

import type { LogItem } from "@chirpstack/chirpstack-api-grpc-web/api/internal_pb";

import { useFormatDisplayTimestamp } from "../contexts/DisplayDateTimeContext";

interface IProps {
  logs: LogItem[];
}

function IntegrationEventTable(props: IProps) {
  const formatTs = useFormatDisplayTimestamp();
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [body, setBody] = useState<string | null>(null);
  const [drawerTitle, setDrawerTitle] = useState<string | null>(null);

  const onDrawerClose = () => {
    setDrawerOpen(false);
  };

  const onDrawerOpen = (time: { seconds: number } | undefined, body: string) => {
    const title = formatTs(time?.seconds);

    return () => {
      setBody(body);
      setDrawerTitle(title);
      setDrawerOpen(true);
    };
  };

  const downloadSingle = () => {
    fileDownload(JSON.stringify(JSON.parse(body!), null, 4), "single-log.json", "application/json");
  };

  const downloadAll = () => {
    const items = props.logs.map((l, _i) => JSON.parse(l.getBody()));
    fileDownload(JSON.stringify(items, null, 4), "log.json");
  };

  const items = props.logs.map((l, _i) => l.toObject());
  const bodyJson = JSON.parse(body!);

  const theme = {
    scheme: "google",
    author: "seth wright (http://sethawright.com)",
    base00: "#ffffff",
    base01: "#282a2e",
    base02: "#373b41",
    base03: "#969896",
    base04: "#b4b7b4",
    base05: "#c5c8c6",
    base06: "#e0e0e0",
    base07: "#ffffff",
    base08: "#CC342B",
    base09: "#F96A38",
    base0A: "#FBA922",
    base0B: "#198844",
    base0C: "#3971ED",
    base0D: "#3971ED",
    base0E: "#A36AC7",
    base0F: "#3971ED",
  };

  const getProp = (obj: { propertiesMap: [string, string][] }, key: string): string => {
    return obj.propertiesMap.find((p: [string, string]) => p[0] === key)?.[1] ?? "";
  };

  const isError = (obj: { propertiesMap: [string, string][] }) => {
    const success = getProp(obj, "Success");
    const status = Number(getProp(obj, "HTTP status"));
    return success.toLowerCase() === "false" || (!Number.isNaN(status) && status >= 400);
  };

  // The backend emits "Application ID" (the API keeps upstream naming); the
  // console calls the entity a Network, so remap the label for display only.
  const displayLabels: Record<string, string> = { "Application ID": "Network ID" };

  const compactTags = (obj: { propertiesMap: [string, string][] }) => {
    const keys = ["Application ID", "Endpoint", "Error", "HTTP status", "Latency (ms)"];
    const tags = keys
      .map(k => [k, getProp(obj, k)] as [string, string])
      .filter(([, v]) => v !== "")
      .slice(0, 4)
      .map(([k, v]) => [displayLabels[k] ?? k, v] as [string, string]);
    const nonEmptyCount = obj.propertiesMap.filter(p => p[1] !== "").length;
    const more = Math.max(nonEmptyCount - tags.length, 0);
    return { tags, more };
  };

  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      <Drawer
        title={`Details: ${drawerTitle}`}
        placement="right"
        size={650}
        onClose={onDrawerClose}
        open={drawerOpen}
        extra={<Button onClick={downloadSingle}>Download</Button>}
      >
        <JSONTreeOriginal
          data={bodyJson}
          theme={theme}
          hideRoot={true}
          shouldExpandNodeInitially={() => {
            return true;
          }}
        />
      </Drawer>
      {items.length !== 0 && (
        <Space orientation="horizontal" style={{ float: "right" }} size="large">
          <Spin size="small" />
          <Button onClick={downloadAll}>Download</Button>
        </Space>
      )}
      <div style={{ width: "100%", overflowX: "hidden" }}>
        <Table
          rowKey="id"
          showHeader={false}
          loading={items.length === 0}
          dataSource={items}
          pagination={false}
          tableLayout="fixed"
          style={{ width: "100%" }}
          columns={[
            {
              title: "Time",
              dataIndex: "time",
              key: "time",
              width: 170,
              render: (_text, obj) => (
                <span style={{ whiteSpace: "nowrap" }} title={formatTs(obj.time?.seconds)}>
                  {formatTs(obj.time?.seconds)}
                </span>
              ),
            },
            {
              title: "Type",
              dataIndex: "description",
              key: "description",
              width: 180,
              render: (text, obj) => (
                <Space size={8}>
                  {isError(obj as { propertiesMap: [string, string][] }) && <Tag color="error">Error</Tag>}
                  <Button
                    icon={<ZoomInOutlined />}
                    type={isError(obj as { propertiesMap: [string, string][] }) ? "default" : "primary"}
                    danger={isError(obj as { propertiesMap: [string, string][] })}
                    shape="round"
                    size="small"
                    onClick={onDrawerOpen(obj.time, obj.body)}
                  >
                    {text}
                  </Button>
                </Space>
              ),
            },
            {
              title: "Properties",
              dataIndex: "properties",
              key: "properties",
              onCell: () => ({
                style: {
                  paddingLeft: 16,
                },
              }),
              render: (_text, obj) => {
                const { tags, more } = compactTags(obj as { propertiesMap: [string, string][] });
                return (
                  <Space orientation="horizontal" size="small" wrap style={{ maxWidth: "100%" }}>
                    {tags.map(([k, v]) => (
                      <Tooltip key={k} title={`${k}: ${v}`}>
                        <Tag variant="outlined">
                          <span
                            style={{
                              display: "inline-block",
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              verticalAlign: "bottom",
                            }}
                          >
                            {k}: {v}
                          </span>
                        </Tag>
                      </Tooltip>
                    ))}
                    {more > 0 && <Tag variant="outlined">+{more} more</Tag>}
                  </Space>
                );
              },
            },
          ]}
        />
      </div>
    </Space>
  );
}

export default IntegrationEventTable;
